import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    IconButton,
    Stack,
    Paper,
    Divider,
    Chip,
    Tooltip,
    Alert,
    InputAdornment,
    Fade
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Calculate as CalculateIcon,
    Clear as ClearIcon,
    AttachMoney as MoneyIcon,
    TrendingUp as TrendingUpIcon,
    Info as InfoIcon
} from '@mui/icons-material';

const ParlayCalculator = () => {
    const [legs, setLegs] = useState([
        { id: 1, odds: '', description: '' },
        { id: 2, odds: '', description: '' }
    ]);
    const [stake, setStake] = useState('');
    const [nextId, setNextId] = useState(3);

    // Convert American odds to decimal
    const americanToDecimal = (americanOdds) => {
        const odds = parseFloat(americanOdds);
        if (isNaN(odds)) return null;
        
        if (odds > 0) {
            return (odds / 100) + 1;
        } else if (odds < 0) {
            return (100 / Math.abs(odds)) + 1;
        }
        return null;
    };

    // Convert decimal odds back to American for display
    const decimalToAmerican = (decimalOdds) => {
        if (decimalOdds >= 2) {
            return `+${Math.round((decimalOdds - 1) * 100)}`;
        } else {
            return Math.round(-100 / (decimalOdds - 1)).toString();
        }
    };

    // Calculate parlay results
    const calculateParlay = useCallback(() => {
        const validLegs = legs.filter(leg => {
            const odds = parseFloat(leg.odds);
            return !isNaN(odds) && odds !== 0;
        });

        if (validLegs.length < 2) {
            return { isValid: false, error: 'Enter at least 2 valid odds' };
        }

        let totalDecimalOdds = 1;
        for (const leg of validLegs) {
            const decimalOdds = americanToDecimal(leg.odds);
            if (!decimalOdds) {
                return { isValid: false, error: 'Invalid odds format' };
            }
            totalDecimalOdds *= decimalOdds;
        }

        const stakeAmount = parseFloat(stake) || 0;
        const potentialPayout = stakeAmount * totalDecimalOdds;
        const profit = potentialPayout - stakeAmount;

        return {
            isValid: true,
            totalDecimalOdds: totalDecimalOdds.toFixed(2),
            totalAmericanOdds: decimalToAmerican(totalDecimalOdds),
            potentialPayout: potentialPayout.toFixed(2),
            profit: profit.toFixed(2),
            legsCount: validLegs.length,
            impliedProbability: ((1 / totalDecimalOdds) * 100).toFixed(2)
        };
    }, [legs, stake]);

    const result = calculateParlay();

    const handleAddLeg = () => {
        setLegs(prev => [...prev, { id: nextId, odds: '', description: '' }]);
        setNextId(prev => prev + 1);
    };

    const handleRemoveLeg = (id) => {
        if (legs.length > 2) {
            setLegs(prev => prev.filter(leg => leg.id !== id));
        }
    };

    const handleOddsChange = (id, value) => {
        // Allow only numbers, minus sign, and plus sign
        const sanitized = value.replace(/[^0-9+-]/g, '');
        setLegs(prev => prev.map(leg => 
            leg.id === id ? { ...leg, odds: sanitized } : leg
        ));
    };

    const handleDescriptionChange = (id, value) => {
        setLegs(prev => prev.map(leg => 
            leg.id === id ? { ...leg, description: value } : leg
        ));
    };

    const handleClearAll = () => {
        setLegs([
            { id: nextId, odds: '', description: '' },
            { id: nextId + 1, odds: '', description: '' }
        ]);
        setNextId(prev => prev + 2);
        setStake('');
    };

    const formatCurrency = (value) => {
        const num = parseFloat(value);
        if (isNaN(num)) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(num);
    };

    return (
        <Card 
            elevation={2}
            sx={{ 
                overflow: 'visible',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                }
            }}
        >
            <CardHeader
                avatar={<CalculateIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
                title={
                    <Box>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Parlay Calculator
                        </Typography>
                        <Chip
                            label="Betting Tool"
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>
                }
                action={
                    <Tooltip title="Clear all">
                        <IconButton onClick={handleClearAll} color="error">
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                }
                sx={{ pb: 1 }}
            />

            <CardContent sx={{ pt: 0 }}>
                {/* Info Alert */}
                <Alert 
                    severity="info" 
                    icon={<InfoIcon />}
                    sx={{ mb: 3 }}
                >
                    Enter American odds (e.g., -110, +150) for each leg. Add at least 2 selections to calculate your parlay.
                </Alert>

                {/* Legs Section */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Parlay Legs ({legs.length})
                </Typography>

                <Stack spacing={2} sx={{ mb: 3 }}>
                    {legs.map((leg, index) => (
                        <Fade in key={leg.id}>
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    border: '2px solid',
                                    borderColor: leg.odds && americanToDecimal(leg.odds) ? 'success.light' : 'grey.300',
                                    borderRadius: 2,
                                    bgcolor: 'white',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <Stack 
                                    direction={{ xs: 'column', sm: 'row' }} 
                                    spacing={2} 
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                >
                                    <Chip 
                                        label={`Leg ${index + 1}`} 
                                        size="small" 
                                        color="primary"
                                        variant="outlined"
                                        sx={{ 
                                            fontWeight: 600,
                                            alignSelf: { xs: 'flex-start', sm: 'center' }
                                        }}
                                    />
                                    
                                    <TextField
                                        size="small"
                                        label="Bet Description"
                                        placeholder="e.g., Lakers ML, Over 225.5"
                                        value={leg.description}
                                        onChange={(e) => handleDescriptionChange(leg.id, e.target.value)}
                                        sx={{ 
                                            flex: { xs: 1, sm: 2 },
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'white',
                                                '& fieldset': {
                                                    borderColor: 'success.main',
                                                    borderWidth: '2px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'success.dark'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'success.dark'
                                                }
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000000 !important'
                                            },
                                            '& input': {
                                                color: '#000000 !important'
                                            }
                                        }}
                                    />
                                    
                                    <TextField
                                        size="small"
                                        label="Odds"
                                        placeholder="-110 or +150"
                                        value={leg.odds}
                                        onChange={(e) => handleOddsChange(leg.id, e.target.value)}
                                        inputProps={{ 
                                            inputMode: 'text',
                                            pattern: '[+-]?[0-9]*'
                                        }}
                                        sx={{ 
                                            width: { xs: '100%', sm: 140 },
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'white',
                                                '& fieldset': {
                                                    borderColor: 'success.main',
                                                    borderWidth: '2px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'success.dark'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'success.dark'
                                                },
                                                '&.Mui-error fieldset': {
                                                    borderColor: 'error.main'
                                                }
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#000000 !important'
                                            },
                                            '& input': {
                                                color: '#000000 !important'
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TrendingUpIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            )
                                        }}
                                        error={leg.odds !== '' && !americanToDecimal(leg.odds)}
                                        helperText={leg.odds !== '' && !americanToDecimal(leg.odds) ? 'Invalid odds' : ''}
                                    />
                                    
                                    <Tooltip title={legs.length <= 2 ? "Minimum 2 legs required" : "Remove leg"}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveLeg(leg.id)}
                                                disabled={legs.length <= 2}
                                                color="error"
                                                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Paper>
                        </Fade>
                    ))}
                </Stack>

                {/* Add Leg Button */}
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddLeg}
                    fullWidth
                    sx={{ 
                        mb: 3,
                        bgcolor: 'warning.main',
                        color: 'white',
                        '&:hover': {
                            bgcolor: 'warning.dark'
                        }
                    }}
                >
                    Add Another Leg
                </Button>

                <Divider sx={{ my: 3 }} />

                {/* Stake Input */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Wager Amount
                </Typography>

                <TextField
                    fullWidth
                    size="medium"
                    label="Wager Amount ($)"
                    placeholder="e.g., 100"
                    value={stake}
                    onChange={(e) => setStake(e.target.value.replace(/[^0-9.]/g, ''))}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <MoneyIcon color="success" />
                            </InputAdornment>
                        )
                    }}
                    sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            fontSize: '1.1rem'
                        },
                        '& .MuiInputBase-input': {
                            color: '#000000 !important'
                        },
                        '& input': {
                            color: '#000000 !important'
                        }
                    }}
                />

                {/* Results Section */}
                {result.isValid ? (
                    <Fade in>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                                Parlay Summary
                            </Typography>

                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Number of Legs:
                                    </Typography>
                                    <Chip 
                                        label={result.legsCount} 
                                        size="small" 
                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                    />
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Odds (American):
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {result.totalAmericanOdds}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Odds (Decimal):
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {result.totalDecimalOdds}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Implied Probability:
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {result.impliedProbability}%
                                    </Typography>
                                </Stack>

                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Stake:
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(stake || 0)}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Potential Profit:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.light' }}>
                                        {formatCurrency(result.profit)}
                                    </Typography>
                                </Stack>

                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        p: 2, 
                                        bgcolor: 'rgba(255,255,255,0.15)', 
                                        borderRadius: 1,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                        Total Payout
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(result.potentialPayout)}
                                    </Typography>
                                </Paper>
                            </Stack>
                        </Paper>
                    </Fade>
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: 'grey.100',
                            borderRadius: 2,
                            textAlign: 'center'
                        }}
                    >
                        <CalculateIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                            {result.error || 'Enter your parlay legs to see potential payout'}
                        </Typography>
                    </Paper>
                )}

                {/* Disclaimer */}
                <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                        display: 'block', 
                        mt: 2, 
                        textAlign: 'center',
                        fontStyle: 'italic'
                    }}
                >
                    * Actual payouts may vary slightly based on sportsbook rounding. Please gamble responsibly.
                </Typography>
            </CardContent>
        </Card>
    );
};

export default ParlayCalculator;
